import { createContext, useContext, useState, ReactNode } from 'react';
import { EpisodeTO } from '../lib';
import { get } from 'lodash';
const dosageInstructionValuePath = 'detail.fhir.dosageInstruction.text';
const orderStatusCodeValuePath = 'detail.orderstatuscode';
const requiredOrderStatusCode = 'AUA1';

interface Medication {
  insights: unknown[];
  events: {
    episode_id: number;
    event_date: string;
  }[];
  medName: string;
  dosageInstruction?: string;
  orderStatusCode?: string;
}

export interface Patient {
  medications?: Record<string, Record<string, Medication>>;
  episodes?: EpisodeTO[];
  latestEpisodeId?: number | null;
}

interface PatientDetailContextValue {
  patient: Patient | null;
  setPatient: (p: Patient | null) => void;
  updatePatient: (partial: Partial<Patient>) => void;
  medsForLatestEpisode: Medication[];
}

const PatientDetailContext = createContext<
  PatientDetailContextValue | undefined
>(undefined);

export const PatientDetailProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [patient, setPatient] = useState<Patient | null>(null);

  const updatePatient = (partial: Partial<Patient>) => {
    setPatient((prev) =>
      prev ? { ...prev, ...partial } : (partial as Patient),
    );
  };
  const latestEpisodeId = patient?.latestEpisodeId;
  const allMeds = Object.entries(patient?.medications || {})
    .map(([key, value]) => {
      return Object.entries(value).map(([key, value]) => {
        return {
          ...value,
          medName: key,
        };
      });
    })
    .reduce<Medication[]>((acc, cur) => {
      return [...acc, ...Object.values(cur)];
    }, []);
  const medsForLatestEpisode = allMeds.filter((med: Medication) => {
    const events = med.events;
    return events.some((event: any) => {
      const sameEpisode = event.episode_id === latestEpisodeId;
      if (!sameEpisode) return false;
      const status: string = String(
        get(event, 'detail.orderStatusCode', '') ||
          get(event, orderStatusCodeValuePath, '') ||
          '',
      );
      return status === requiredOrderStatusCode;
    });
  });
  const withDosageInstruction = medsForLatestEpisode.map((med) => {
    const events = Array.isArray(med?.events) ? med?.events.slice() : [];
    const sortedEvents = events.sort((a, b) => {
      const timeA = Date.parse(a.event_date); // or +new Date(a.event_date)
      const timeB = Date.parse(b.event_date);

      return timeB - timeA; // >0 → b before a  (latest first)
    });
    const relevantEvents = sortedEvents.filter((event: any) => {
      if (event?.episode_id !== latestEpisodeId) return false;
      const status: string = String(
        get(event, 'detail.orderStatusCode', '') ||
          get(event, orderStatusCodeValuePath, '') ||
          '',
      );
      return status === requiredOrderStatusCode;
    });
    const dosageInstructionEvent = relevantEvents.find((event) =>
      get(event, dosageInstructionValuePath, ''),
    );
    const dosageInstructionText = get(
      dosageInstructionEvent,
      dosageInstructionValuePath,
      '',
    );
    const orderStatusCodeEvent = relevantEvents.find(
      (event) =>
        get(event, 'detail.orderStatusCode', '') ||
        get(event, orderStatusCodeValuePath, ''),
    );
    const orderStatusCode =
      get(orderStatusCodeEvent, 'detail.orderStatusCode', '') ||
      get(orderStatusCodeEvent, orderStatusCodeValuePath, '') ||
      undefined;
    return {
      ...med,
      dosageInstruction: dosageInstructionText,
      orderStatusCode,
    };
  });

  const value: PatientDetailContextValue = {
    patient,
    setPatient,
    updatePatient,
    medsForLatestEpisode: withDosageInstruction,
  };

  return (
    <PatientDetailContext.Provider value={value}>
      {children}
    </PatientDetailContext.Provider>
  );
};

export const usePatientDetail = () => {
  const ctx = useContext(PatientDetailContext);
  if (!ctx) {
    throw new Error(
      'usePatientDetail must be used within a PatientDetailProvider',
    );
  }
  return ctx;
};
